import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeams, createTeam, deleteTeam, Team, getPendingInvitations, acceptInvitation, TeamInvitation, getAllUsersWithRoles, updateUserRole } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassModal } from '../ui/GlassModal';
import { GlassTabs } from '../ui/GlassTabs';
import { ModuleHeader } from '../ui/ModuleHeader';
import { Plus, Users, Trash2, Eye, Shield } from 'lucide-react';
import './Teams.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// Role badge helper — maps system role to GlassBadge variant
const getRoleBadgeVariant = (role: string): 'success' | 'warning' | 'info' | 'default' => {
    switch (role) {
        case 'owner': return 'info';
        case 'admin': return 'warning';
        case 'editor': return 'success';
        case 'viewer': return 'default';
        default: return 'default';
    }
};

const SYSTEM_ROLE_OPTIONS = [
    { value: 'owner', label: 'Owner' },
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
    { value: 'user', label: 'User' },
];

const PAGE_TABS = [
    { id: 'teams', label: 'My Teams' },
    { id: 'roles', label: 'User Roles' },
];

// ── User Roles Panel ──────────────────────────────────────────────────────────
const UserRolesPanel: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner';

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError('');
            try {
                const data = await getAllUsersWithRoles();
                setUsers(data);
            } catch (err: any) {
                setError(getApiError(err, 'Failed to load users'));
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleRoleChange = async (userId: string, newRole: string) => {
        setUpdatingUserId(userId);
        try {
            const updated = await updateUserRole(userId, newRole);
            setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: updated.role } : u));
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update role'));
        } finally {
            setUpdatingUserId(null);
        }
    };

    if (isLoading) {
        return <GlassCard variant="default" padding="lg"><div className="loading" style={{ color: 'var(--text-secondary)' }}>Loading users...</div></GlassCard>;
    }

    if (!isAdmin) {
        return (
            <GlassCard variant="default" padding="lg">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', color: 'var(--text-secondary)' }}>
                    <Shield size={20} />
                    <span>Only admins and owners can manage user roles.</span>
                </div>
            </GlassCard>
        );
    }

    return (
        <GlassCard variant="default" padding="lg">
            {error && <div style={{ color: 'var(--danger)', marginBottom: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>{error}</div>}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>User</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Role</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', borderBottom: '1px solid var(--divider)' }}>Member Since</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.user_id} style={{ transition: 'background var(--transition-fast)' }}>
                                <td style={{ padding: '12px', borderBottom: '1px solid var(--divider)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-full)', background: 'var(--primary-500)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', flexShrink: 0 }}>
                                            {(u.full_name || u.username || u.email || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--text)', fontSize: 'var(--text-sm)' }}>
                                            {u.full_name || u.username}
                                        </span>
                                    </div>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid var(--divider)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                    {u.email}
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid var(--divider)' }}>
                                    {u.user_id === currentUser?.user_id ? (
                                        <GlassBadge variant={getRoleBadgeVariant(u.role)} size="sm">{u.role}</GlassBadge>
                                    ) : updatingUserId === u.user_id ? (
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Updating...</span>
                                    ) : (
                                        <GlassSelect
                                            options={SYSTEM_ROLE_OPTIONS}
                                            value={u.role}
                                            onChange={(newRole) => handleRoleChange(u.user_id, newRole)}
                                            size="sm"
                                        />
                                    )}
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid var(--divider)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </GlassCard>
    );
};

// ── Main TeamsList ─────────────────────────────────────────────────────────────
const TeamsList: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('teams');
    const [teams, setTeams] = useState<Team[]>([]);
    const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamDescription, setNewTeamDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const navigate = useNavigate();

    const loadData = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [teamsData, invitationsData] = await Promise.all([
                getTeams(),
                getPendingInvitations()
            ]);
            setTeams(teamsData);
            setInvitations(invitationsData);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to load teams'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleCreateTeam = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!newTeamName.trim()) return;

        setIsCreating(true);
        try {
            const team = await createTeam({
                name: newTeamName.trim(),
                description: newTeamDescription.trim() || undefined
            });
            setTeams(prev => [...prev, team]);
            setShowCreateModal(false);
            setNewTeamName('');
            setNewTeamDescription('');
            navigate(`/teams/${team.team_id}`);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to create team'));
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteTeam = async (teamId: string, teamName: string) => {
        if (!window.confirm(`Are you sure you want to delete the team "${teamName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteTeam(teamId);
            setTeams(prev => prev.filter(t => t.team_id !== teamId));
        } catch (err: any) {
            setError(getApiError(err, 'Failed to delete team'));
        }
    };

    const handleAcceptInvitation = async (token: string) => {
        try {
            await acceptInvitation(token);
            loadData(); // Reload to get updated teams list
        } catch (err: any) {
            setError(getApiError(err, 'Failed to accept invitation'));
        }
    };

    if (isLoading) {
        return (
            <div className="teams-page">
                <ModuleHeader title="My Teams" />
                <GlassCard variant="default" padding="lg" className="loading-card">
                    <div className="loading">Loading teams...</div>
                </GlassCard>
            </div>
        );
    }

    return (
        <div className="teams-page">
            <ModuleHeader
                title="Teams & Collaboration"
                breadcrumb={['Workspace']}
                actions={activeTab === 'teams' ? [
                    <GlassButton
                        key="create"
                        variant="primary"
                        onClick={() => setShowCreateModal(true)}
                        icon={<Plus size={18} />}
                    >
                        Create Team
                    </GlassButton>
                ] : []}
            />

            <div style={{ marginBottom: 'var(--space-4)' }}>
                <GlassTabs tabs={PAGE_TABS} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {activeTab === 'roles' && <UserRolesPanel />}
            {activeTab === 'teams' && (<>

            {error && (
                <GlassCard variant="default" padding="md" className="error-card">
                    <div className="error-message">{error}</div>
                </GlassCard>
            )}

            {/* Pending Invitations */}
            {invitations.length > 0 && (
                <div className="invitations-section">
                    <h2>Pending Invitations</h2>
                    <div className="invitations-list">
                        {invitations.map(inv => (
                            <GlassCard 
                                key={inv.invitation_id} 
                                variant="strong" 
                                padding="md"
                                className="invitation-card"
                            >
                                <div className="invitation-info">
                                    <strong>{inv.team_name}</strong>
                                    <div className="invitation-meta">
                                        <GlassBadge variant="info" size="sm">{inv.role}</GlassBadge>
                                        <span className="invited-by">Invited by: {inv.invited_by_name}</span>
                                    </div>
                                </div>
                                <div className="invitation-actions">
                                    <GlassButton
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleAcceptInvitation(inv.invitation_id)}
                                    >
                                        Accept
                                    </GlassButton>
                                </div>
                            </GlassCard>
                        ))}
                    </div>
                </div>
            )}

            {/* Teams List */}
            {teams.length === 0 ? (
                <GlassCard variant="default" padding="lg" className="empty-state-card">
                    <div className="empty-state">
                        <div className="empty-icon">
                            <Users size={48} />
                        </div>
                        <h2>No teams yet</h2>
                        <p>Create a team to start collaborating with others on your projects.</p>
                        <GlassButton
                            variant="primary"
                            onClick={() => setShowCreateModal(true)}
                            icon={<Plus size={18} />}
                        >
                            Create Your First Team
                        </GlassButton>
                    </div>
                </GlassCard>
            ) : (
                <div className="teams-grid">
                    {teams.map(team => (
                        <GlassCard
                            key={team.team_id}
                            variant="hover"
                            padding="md"
                            className="team-card"
                            onClick={() => navigate(`/teams/${team.team_id}`)}
                        >
                            <div className="team-card-header">
                                <div className="team-avatar">
                                    {team.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="team-card-actions">
                                    <GlassButton
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e?: React.MouseEvent) => {
                                            e?.stopPropagation();
                                            navigate(`/teams/${team.team_id}`);
                                        }}
                                        icon={<Eye size={16} />}
                                        title="View team"
                                    />
                                    <GlassButton
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e?: React.MouseEvent) => {
                                            e?.stopPropagation();
                                            handleDeleteTeam(team.team_id, team.name);
                                        }}
                                        title="Delete team"
                                        icon={<Trash2 size={16} />}
                                    />
                                </div>
                            </div>
                            <h3 className="team-name">{team.name}</h3>
                            {team.description && (
                                <p className="team-description">{team.description}</p>
                            )}
                            <div className="team-stats">
                                <div className="stat">
                                    <span className="stat-value">{team.member_count || 1}</span>
                                    <span className="stat-label">member{(team.member_count || 1) !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="stat-divider"></div>
                                <div className="stat">
                                    <span className="stat-value">{team.project_count || 0}</span>
                                    <span className="stat-label">project{(team.project_count || 0) !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}

            </>)}

            {/* Create Team Modal */}
            <GlassModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create New Team"
                size="md"
                footer={
                    <>
                        <GlassButton
                            variant="ghost"
                            onClick={() => setShowCreateModal(false)}
                        >
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={handleCreateTeam}
                            loading={isCreating}
                            disabled={isCreating || !newTeamName.trim()}
                        >
                            Create Team
                        </GlassButton>
                    </>
                }
            >
                <form onSubmit={handleCreateTeam}>
                    <GlassInput
                        label="Team Name"
                        value={newTeamName}
                        onChange={setNewTeamName}
                        placeholder="Enter team name"
                        autoFocus
                    />
                    <div style={{ marginTop: 'var(--space-4)' }}>
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            value={newTeamDescription}
                            onChange={e => setNewTeamDescription(e.target.value)}
                            placeholder="What is this team for?"
                            rows={3}
                            className="glass-textarea"
                        />
                    </div>
                </form>
            </GlassModal>

            <style>{`
                .teams-page {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: var(--space-4);
                }

                .loading-card,
                .error-card {
                    text-align: center;
                }

                .loading {
                    color: var(--text-secondary);
                }

                .error-message {
                    color: var(--danger);
                }

                .invitations-section {
                    margin-bottom: var(--space-6);
                }

                .invitations-section h2 {
                    font-size: var(--text-lg);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                    margin: 0 0 var(--space-4) 0;
                }

                .invitations-list {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-3);
                }

                .invitation-card {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .invitation-info {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-2);
                }

                .invitation-info strong {
                    color: var(--text);
                    font-size: var(--text-base);
                }

                .invitation-meta {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                }

                .invited-by {
                    font-size: var(--text-sm);
                    color: var(--text-secondary);
                }

                .empty-state-card {
                    text-align: center;
                }

                .empty-state {
                    padding: var(--space-8);
                }

                .empty-icon {
                    color: var(--text-secondary);
                    margin-bottom: var(--space-4);
                    display: flex;
                    justify-content: center;
                }

                .empty-state h2 {
                    margin: 0 0 var(--space-2) 0;
                    color: var(--text);
                    font-size: var(--text-xl);
                }

                .empty-state p {
                    margin: 0 0 var(--space-6) 0;
                    color: var(--text-secondary);
                }

                .teams-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: var(--space-4);
                }

                .team-card {
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .team-card:hover {
                    transform: translateY(-4px);
                }

                .team-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: var(--space-3);
                }

                .team-avatar {
                    width: 48px;
                    height: 48px;
                    border-radius: var(--radius-full);
                    background: var(--primary-500);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: var(--text-xl);
                    font-weight: var(--font-semibold);
                    border: 2px solid var(--stroke);
                    box-shadow: var(--shadow);
                }

                .team-card-actions {
                    display: flex;
                    gap: var(--space-2);
                }

                .team-name {
                    margin: 0 0 var(--space-2) 0;
                    font-size: var(--text-lg);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .team-description {
                    color: var(--text-secondary);
                    font-size: var(--text-sm);
                    margin: 0 0 var(--space-4) 0;
                    line-height: 1.5;
                }

                .team-stats {
                    display: flex;
                    align-items: center;
                    gap: var(--space-4);
                    padding-top: var(--space-3);
                    border-top: 1px solid var(--divider);
                }

                .stat {
                    display: flex;
                    align-items: center;
                    gap: var(--space-1);
                }

                .stat-value {
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .stat-label {
                    font-size: var(--text-sm);
                    color: var(--text-secondary);
                }

                .stat-divider {
                    width: 1px;
                    height: 16px;
                    background: var(--divider);
                }

                .form-label {
                    display: block;
                    font-size: var(--text-sm);
                    font-weight: var(--font-medium);
                    color: var(--text-secondary);
                    margin-bottom: var(--space-1);
                }

                .glass-textarea {
                    width: 100%;
                    padding: var(--space-3) var(--space-4);
                    font-size: var(--text-base);
                    font-family: var(--font-family);
                    color: var(--text);
                    background: var(--panel);
                    border: 1px solid var(--stroke);
                    border-radius: var(--radius-md);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    transition: all var(--transition-normal);
                    outline: none;
                    resize: vertical;
                    box-sizing: border-box;
                }

                .glass-textarea:focus {
                    border-color: var(--primary-500);
                    box-shadow: 0 0 0 3px var(--primary-100);
                }

                @media (max-width: 768px) {
                    .teams-page {
                        padding: var(--space-2);
                    }

                    .teams-grid {
                        grid-template-columns: 1fr;
                    }

                    .invitation-card {
                        flex-direction: column;
                        gap: var(--space-3);
                        align-items: flex-start;
                    }
                }
            `}</style>
        </div>
    );
};

export default TeamsList;
